import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { FilterOperators } from '../enums/filter-operators.enum';
import { FilterTypes } from '../enums/filter-types.enum';
import { AGILOX_API_ENDPOINT } from '../tokens/agilox-api-endpoint.token';
import { Response } from '../interfaces/response.interface';
import { Vehicle } from '../interfaces/vehicle.interface';
import { HttpParamsBuilder } from '../utils/params-builder.util';

const FETCH_BY_IDS_PAGE_SIZE = 300;

@Injectable({
	providedIn: 'root',
})
export class VehiclesApiService {
	private http = inject(HttpClient);
	private apiEndpoint = inject(AGILOX_API_ENDPOINT);

	private static readonly MAX_CACHE_SIZE = 20;
	private cachedRequests = new Map<string, Observable<Response<Vehicle>>>();

	fetchByIds(vehicleIds: string[]): Observable<Vehicle[]> {
		const params = HttpParamsBuilder.buildParameters(
			{ number: 0, size: FETCH_BY_IDS_PAGE_SIZE },
			undefined,
			undefined,
			undefined,
			[
				{
					property: 'vehicle_uuid',
					operator: FilterOperators.EQUAL,
					value: vehicleIds.join(','),
					type: FilterTypes.TEXT,
				},
			]
		);
		return this.fetch(params).pipe(map((response) => response.data));
	}

	fetch(params: HttpParams): Observable<Response<Vehicle>> {
		const key = params.toString();
		const cached = this.cachedRequests.get(key);
		if (cached) {
			return cached;
		}

		if (this.cachedRequests.size >= VehiclesApiService.MAX_CACHE_SIZE) {
			const oldestKey = this.cachedRequests.keys().next().value;
			this.cachedRequests.delete(oldestKey!);
		}

		const request$ = this.http
			.get<Response<Vehicle>>(this.apiEndpoint + '/v2/machineMgmt/vehicles', { params })
			.pipe(
				tap({
					error: () => this.cachedRequests.delete(key),
				}),
				shareReplay(1)
			);

		this.cachedRequests.set(key, request$);
		return request$;
	}
}
